/**
 * Lobby Firestore operations for the platform. Pure async functions —
 * no React, no hooks. UI layers call these to mutate room state and
 * subscribe to live updates.
 *
 * All Firestore paths flow through `src/platform/paths.ts`; never hardcode
 * a path string in here.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore'
import { getDb } from '@/platform/firebase/client'
import {
  dbcRooms,
  dbdRoom,
  dbdRoomPrivate,
  dbdRoomState,
  generateRoomCode,
} from '@mdev/common'
import type { GameId, PlayerColor, PlayerLobbyInfo, Room } from '@mdev/common'
import {
  RoomCodeCollisionError,
  RoomFullError,
  RoomLockedError,
  RoomNotFoundError,
} from './errors'

const db = getDb()

// --- Types ----------------------------------------------------------------

export interface CreateRoomOpts<TSettings> {
  gameId: GameId
  hostUid: string
  /** Defaulted by the caller from `GameModule.defaultSettings`. */
  gameSettings: TSettings
  /** From `GameModule.maxPlayers`. Optional — capacity is enforced in `joinRoom`. */
  maxPlayers?: number
}

export interface JoinProfile {
  displayName: string
  color: PlayerColor
  avatar: string
}

const MAX_CODE_GEN_ATTEMPTS = 20

// --- Helpers --------------------------------------------------------------

function roomDocRef( code: string ) {
  return doc( db, ...dbdRoom( code ) )
}

function roomStateDocRef( code: string ) {
  return doc( db, ...dbdRoomState( code ) )
}

function roomsCollectionRef() {
  return collection( db, ...dbcRooms() )
}

async function fetchRoom<TSettings>( code: string ): Promise<Room<TSettings>> {
  const snap = await getDoc( roomDocRef( code ) )
  if ( !snap.exists() ) throw new RoomNotFoundError( code )
  return snap.data() as Room<TSettings>
}

// --- Operations -----------------------------------------------------------

/**
 * Create a new room with a freshly-generated unique code.
 *
 * The host's laptop is a pure display (the "TV") and is NOT a player: it
 * renders the shared board in `HostShell` and never submits moves. The room
 * therefore starts with an empty `players` map — every participant (Mr. X +
 * detectives) joins from their own phone via `joinRoom`. `hostUid` is kept
 * only to identify which client owns the display / Start button.
 */
export async function createRoom<TSettings>(
  opts: CreateRoomOpts<TSettings>,
): Promise<{ code: string }> {
  const { gameId, hostUid, gameSettings } = opts

  // Collision-resistant code allocation: try up to N times.
  let code: string | null = null
  for ( let i = 0; i < MAX_CODE_GEN_ATTEMPTS; i++ ) {
    const candidate = generateRoomCode()
    const existing = await getDoc( roomDocRef( candidate ) )
    if ( !existing.exists() ) {
      code = candidate
      break
    }
  }
  if ( code === null ) throw new RoomCodeCollisionError( MAX_CODE_GEN_ATTEMPTS )

  const room: Room<TSettings> = {
    code,
    gameId,
    status: 'lobby',
    hostUid,
    createdAtMs: Date.now(),
    startedAtMs: null,
    finishedAtMs: null,
    settings: gameSettings,
    // Empty: the host is a display, not a player. Phones populate this via joinRoom.
    players: {},
    winner: null,
  }

  await setDoc( roomDocRef( code ), room as unknown as DocumentData )
  return { code }
}

/**
 * Host-driven "play again": create a fresh room (same game + settings, new
 * host-display id) and stamp its code onto the OLD room as `nextCode`, so every
 * finished player can follow the host into the same new lobby. Returns the new
 * room code (the host navigates to its host page).
 */
export async function rematch<TSettings>(
  oldCode: string,
  gameId: GameId,
  settings: TSettings,
): Promise<{ code: string }> {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${ Date.now() }-${ Math.floor( Math.random() * 1e9 ) }`
  const { code } = await createRoom<TSettings>( {
    gameId,
    hostUid: `host-${ rand }`,
    gameSettings: settings,
  } )
  await updateDoc( roomDocRef( oldCode ), { nextCode: code } )
  return { code }
}

/**
 * Join (or reconnect to) a room. If the uid is already on the room we treat
 * the call as a reconnect/profile-update. Otherwise we add a new player —
 * but only if the room is in lobby and not at capacity.
 */
export async function joinRoom(
  code: string,
  uid: string,
  profile: JoinProfile,
  opts: { maxPlayers: number },
): Promise<void> {
  const room = await fetchRoom( code )

  // The host laptop is a display, not a player. If the host's own uid hits
  // this path (e.g. it opened a player URL on the same device), never enrol
  // it into `players` — that's what was polluting the lobby roster.
  if ( uid === room.hostUid ) return

  const isExistingPlayer = Boolean( room.players[ uid ] )

  if ( !isExistingPlayer ) {
    if ( room.status !== 'lobby' ) throw new RoomLockedError( code, room.status )
    const playerCount = Object.keys( room.players ).length
    if ( playerCount >= opts.maxPlayers ) throw new RoomFullError( code, opts.maxPlayers )
  }

  // Preserve `ready` flag on reconnect; reset to false for new joins.
  const existing = room.players[ uid ]
  const next: PlayerLobbyInfo = {
    uid,
    displayName: profile.displayName,
    color: profile.color,
    avatar: profile.avatar,
    ready: existing ? existing.ready : false,
    connected: true,
    lastSeenMs: Date.now(),
  }

  await updateDoc( roomDocRef( code ), { [ `players.${ uid }` ]: next } )
}

/**
 * Subscribe to live room updates. The callback fires with `null` if the
 * room is deleted. Returns a Firestore unsubscribe function.
 */
export function subscribeRoom<TSettings>(
  code: string,
  callback: ( room: Room<TSettings> | null ) => void,
): Unsubscribe {
  return onSnapshot( roomDocRef( code ), ( snap ) => {
    if ( !snap.exists() ) {
      callback( null )
      return
    }
    callback( snap.data() as Room<TSettings> )
  } )
}

/**
 * Subscribe to the public game-state subdoc at `rooms/{code}/state/main`. The
 * callback fires with `null` until the doc exists (i.e. before `startGame`)
 * or if it's deleted. Returns a Firestore unsubscribe function.
 */
export function subscribeRoomState<TPublic>(
  code: string,
  callback: ( state: TPublic | null ) => void,
): Unsubscribe {
  return onSnapshot( roomStateDocRef( code ), ( snap ) => {
    if ( !snap.exists() ) {
      callback( null )
      return
    }
    callback( snap.data() as TPublic )
  } )
}

/** Toggle a player's ready state. */
export async function setReady( code: string, uid: string, ready: boolean ): Promise<void> {
  await updateDoc( roomDocRef( code ), { [ `players.${ uid }.ready` ]: ready } )
}

/** Update a player's profile (name/color/avatar) mid-lobby. */
export async function updatePlayerProfile(
  code: string,
  uid: string,
  profile: JoinProfile,
): Promise<void> {
  await updateDoc( roomDocRef( code ), {
    [ `players.${ uid }.displayName` ]: profile.displayName,
    [ `players.${ uid }.color` ]: profile.color,
    [ `players.${ uid }.avatar` ]: profile.avatar,
  } )
}

/** Replace the room's `settings` object. Game-specific shape — caller owns it. */
export async function updateRoomSettings<TSettings>(
  code: string,
  settings: TSettings,
): Promise<void> {
  await updateDoc( roomDocRef( code ), { settings: settings as Record<string, unknown> } )
}

/**
 * Leave the lobby. Removes the player entry from `players`. Only valid in
 * lobby — mid-game "leaving" should pause instead, handled elsewhere.
 */
export async function leaveRoom( code: string, uid: string ): Promise<void> {
  const room = await fetchRoom( code )
  if ( room.status !== 'lobby' ) {
    throw new RoomLockedError( code, room.status )
  }

  const nextPlayers = { ...room.players }
  delete nextPlayers[ uid ]
  await updateDoc( roomDocRef( code ), { players: nextPlayers } )
}

/**
 * Host-initiated removal of a player from the lobby. Same effect as
 * `leaveRoom` (deletes the entry from `players`), named for the host UI. Only
 * valid while the room is still in lobby.
 */
export async function kickPlayer( code: string, uid: string ): Promise<void> {
  await leaveRoom( code, uid )
}

/** Heartbeat — update the player's `lastSeenMs` and mark them connected. */
export async function heartbeat( code: string, uid: string ): Promise<void> {
  await updateDoc( roomDocRef( code ), {
    [ `players.${ uid }.lastSeenMs` ]: Date.now(),
    [ `players.${ uid }.connected` ]: true,
  } )
}

/**
 * Transition a room from lobby -> active and write the initial game-state
 * documents atomically. The caller is responsible for splitting
 * `GameModule.setupGame`'s output into:
 *   - `gameStateDoc`: the body to write to `rooms/{code}/state/main` (the
 *     public state visible to all clients).
 *   - `privateDocs`: a map of `key -> doc body` written to
 *     `rooms/{code}/private/{key}`. The caller is also responsible for
 *     enforcing per-client read filters (we have no Firestore Rules in v1 —
 *     security is by URL secrecy).
 *
 * The platform layer stays game-agnostic: it doesn't know how Scotland Yard
 * splits Mr. X's secret station from the public state, only that *some* split
 * happens upstream and we write whatever buckets we're handed.
 */
export interface StartGameDocs {
  gameStateDoc: unknown
  privateDocs?: Record<string, unknown>
}

export async function startGame(
  code: string,
  docs: StartGameDocs,
): Promise<void> {
  const batch = writeBatch( db )
  batch.update( roomDocRef( code ), {
    status: 'active',
    startedAtMs: Date.now(),
  } )
  batch.set( roomStateDocRef( code ), docs.gameStateDoc as DocumentData )
  if ( docs.privateDocs ) {
    for ( const [ key, body ] of Object.entries( docs.privateDocs ) ) {
      batch.set( doc( db, ...dbdRoomPrivate( code, key ) ), body as DocumentData )
    }
  }
  await batch.commit()
}

/** Mark a room as finished and record the winning team. */
export async function finishGame(
  code: string,
  winner: { team: string; reason: string },
): Promise<void> {
  await updateDoc( roomDocRef( code ), {
    status: 'finished',
    finishedAtMs: Date.now(),
    winner,
  } )
}

// --- Convenience reads ----------------------------------------------------

/** One-shot read — useful for non-reactive callers (e.g. server-side scripts). */
export async function getRoom<TSettings>( code: string ): Promise<Room<TSettings>> {
  return fetchRoom<TSettings>( code )
}

/**
 * List rooms for a given game id. Currently used by no UI but handy for
 * future "active games" listings; kept here so paths stay centralized.
 */
export async function listRoomsByGame<TSettings>( gameId: GameId ): Promise<Room<TSettings>[]> {
  const q = query( roomsCollectionRef(), where( 'gameId', '==', gameId ) )
  const snap = await getDocs( q )
  return snap.docs.map( ( d ) => d.data() as Room<TSettings> )
}

// Re-export errors for ergonomic imports.
export {
  GameNotActiveError,
  IllegalMoveError,
  MrxStateMissingError,
  RoomCodeCollisionError,
  RoomFullError,
  RoomLockedError,
  RoomNotFoundError,
  RoomNotInGameError,
} from './errors'
